// Ventana de visibilidad web por estado (clients_vehicles_states.web_visibility_days).
// Un vehículo Vendido/Reservado solo se muestra mientras su evento (venta o
// reserva) esté dentro de la ventana del estado; sin configuración se mantiene
// el histórico de 3 días para no cambiar el comportamiento de los tenants que
// nunca la tocaron. Un valor explícito de 0 desactiva la ventana (siempre visible).
//
// ── Importación histórica (goauto-admin: Importación Inteligente) ────────────
// Una automotora puede subir su historia vieja (Excel/PDF de años anteriores) y
// GoAuto la reconstruye: vehículos y ventas de 2021 que nunca vivieron en el
// sistema. Esas filas llegan marcadas con `is_historical_import = true`.
//
// Reconstruir historia NO es vender hoy. Un auto vendido en 2021 no puede
// aparecer en el sitio público como "recién vendido" solo porque la fila se
// insertó esta semana. Por eso el marcador es una puerta DURA: corta antes de
// mirar la ventana, el estado o la fecha.
//
// La marca no se dedujo de la fecha porque la fecha no alcanzaba, por tres
// razones que se midieron contra producción y no se supusieron:
//
//   1. este archivo mira `created_at` y no `sale_date`, así que una venta
//      vieja insertada hoy caía dentro de la ventana igual;
//   2. `isWithinWindow` trata NULL como "sin ventana", y NULL es lo que tienen
//      186 de las 188 automotoras — ahí el histórico quedaba visible siempre;
//   3. y la que subsume a las otras dos: **este sitio no puede leer
//      `vehicles_sales`**. La tabla tiene RLS con policy solo para
//      `authenticated`, así que con la clave anónima devuelve `[]` y la
//      relación embebida `vehicles_sales!vehicle_id(...)` viene `null` en
//      TODOS los vehículos. `latestCreatedAt` nunca tuvo una fila que mirar:
//      la ventana siempre se resolvió por el fallback a `state_updated_at`.
//
// De ahí sale el diseño: **el marcador que decide es el del VEHÍCULO**
// (`vehicles.is_historical_import`), porque `vehicles` es la única de las dos
// tablas que la clave anónima puede leer. El chequeo de los eventos que hay
// más abajo se conserva como segunda línea —cuesta nada y protege si algún día
// se le abre lectura a las ventas— pero hoy no se dispara nunca acá.
//
// Y ojo con el permiso: `vehicles` no tiene grant de tabla para anon sino de
// COLUMNA. Pedir `is_historical_import` sin el GRANT correspondiente no da
// null, da 42501 y deja el sitio sin autos. Lo concede la migración
// 20260910120200 de goautos-admin; por eso ese PR va primero.
//
// `is_historical_import` es `NOT NULL DEFAULT false`: toda fila que existe hoy
// y todo vehículo normal futuro valen `false`, así que nada de lo de abajo
// cambia el comportamiento actual de ningún tenant.

export const DEFAULT_RECENCY_DAYS = 3;

type EventRow = { created_at: string; is_historical_import?: boolean | null };
type EventRel = EventRow[] | EventRow | null | undefined;

export interface VisibilityFields {
  status?: {
    name?: string | null;
    web_visibility_days?: number | null;
  } | null;
  vehicles_sales?: EventRel;
  vehicles_reservations?: EventRel;
  state_updated_at?: string | null;
  /** El vehículo mismo lo creó una importación histórica: nunca existió operativamente. */
  is_historical_import?: boolean | null;
}

const eventRows = (rel: EventRel): EventRow[] => {
  if (!rel) return [];
  return (Array.isArray(rel) ? rel : [rel]).filter((r) => r?.created_at);
};

const isHistoricalRow = (row: EventRow): boolean => row.is_historical_import === true;

// Solo eventos REALES: un evento histórico no abre la ventana de reciente.
const latestCreatedAt = (rel: EventRel): string | undefined => {
  const rows = eventRows(rel).filter((r) => !isHistoricalRow(r));
  if (!rows.length) return undefined;
  return rows.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0].created_at;
};

// Hay eventos y TODOS son importación histórica. Distinto de "no hay eventos":
// sin eventos vale el fallback a state_updated_at, con eventos históricos no.
const onlyHistoricalEvents = (rel: EventRel): boolean => {
  const rows = eventRows(rel);
  return rows.length > 0 && rows.every(isHistoricalRow);
};

/** El vehículo lo creó una importación histórica. */
export const isHistoricalVehicle = (vehicle: {
  is_historical_import?: boolean | null;
}): boolean => vehicle?.is_historical_import === true;

/**
 * El estado actual del vehículo lo produjo un evento importado como historia.
 *
 * Segunda línea de defensa: hoy no se dispara, porque la clave anónima no ve
 * ninguna fila de `vehicles_sales` ni de `vehicles_reservations` y el arreglo
 * llega siempre vacío. Se deja porque no cuesta nada y porque el día que
 * alguien le abra lectura a esas tablas, esto ya está.
 *
 * Lo que sí actúa es `isHistoricalVehicle`: por eso el APPLY marca el vehículo
 * en los casos A y B, y no solo la venta.
 */
export const hasOnlyHistoricalEvents = (vehicle: VisibilityFields): boolean => {
  const name = vehicle.status?.name;
  if (name === 'Vendido') return onlyHistoricalEvents(vehicle.vehicles_sales);
  if (name === 'Reservado') return onlyHistoricalEvents(vehicle.vehicles_reservations);
  return false;
};

/**
 * Puerta dura de "esto es historia, no stock": el vehículo es una
 * reconstrucción, o su estado lo produjo un evento reconstruido.
 *
 * Devuelve false para absolutamente todo lo que existe hoy, porque hoy ninguna
 * fila tiene el marcador en true.
 */
export const isHistoricalImport = (vehicle: VisibilityFields): boolean =>
  isHistoricalVehicle(vehicle) || hasOnlyHistoricalEvents(vehicle);

// Fecha del evento que gobierna la ventana. Cae a state_updated_at cuando el
// estado se cambió sin registrar la venta/reserva formal: sin este fallback el
// vehículo desaparecía de la web en el instante del cambio. NO cae a
// updated_at: se bumpea con cualquier edición y un update masivo (p.ej. un
// backfill) resucitaría en la web todos los vendidos viejos sin registro.
export const latestEventDate = (vehicle: VisibilityFields): string | undefined => {
  const name = vehicle.status?.name;
  const fromEvent =
    name === 'Vendido'
      ? latestCreatedAt(vehicle.vehicles_sales)
      : name === 'Reservado'
        ? latestCreatedAt(vehicle.vehicles_reservations)
        : undefined;
  return fromEvent ?? vehicle.state_updated_at ?? undefined;
};

// NULL/<=0 = sin ventana. Con ventana pero sin fecha, se oculta (conservador).
export const isWithinWindow = (
  eventDateStr: string | undefined,
  days: number | null | undefined
): boolean => {
  if (!days || days <= 0) return true;
  if (!eventDateStr) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  return new Date(eventDateStr) >= cutoff;
};

// Regla de las superficies de listado (página /vehicles y grids del builder).
// SOLO Vendido/Reservado llevan ventana — para el resto de los estados la
// visibilidad la decide show_in_web a secas, igual que siempre. El carousel NO
// usa esta función: su semántica desde b0c5ef8 es NULL = sin ventana y debe
// mantenerse (los carruseles mode='sold' dependen de ella); por eso el carousel
// aplica isHistoricalImport() por su cuenta.
export const passesRecencyWindow = (vehicle: VisibilityFields): boolean => {
  // Primero de todo, y no adentro de la rama Vendido/Reservado: un vehículo
  // reconstruido no se publica en NINGÚN estado. Si el corte viviera más abajo,
  // un histórico que quedara en un estado publicable saldría por la puerta de
  // "este estado no lleva ventana" sin que nadie lo mirara.
  if (isHistoricalImport(vehicle)) return false;
  const name = vehicle.status?.name;
  if (name !== 'Vendido' && name !== 'Reservado') return true;
  // La otra mitad: sin este corte el vehículo caería al fallback
  // state_updated_at — que la importación acaba de tocar al cambiar el estado,
  // o sea "hoy" — y quedaría visible justamente por lo que debía ocultarlo.
  const days = vehicle.status?.web_visibility_days ?? DEFAULT_RECENCY_DAYS;
  return isWithinWindow(latestEventDate(vehicle), days);
};
